import { formatUserFactorModel } from '../utils/factorDataOptions';

// Provider bindings and MSI snapshots are retained internally, but they are
// maintenance of the current factor dataset, not a new user-selected dataset.
export const FACTOR_DATA_UPDATE_FIELDS = Object.freeze([
    'vendor', 'factor_model', 'factor_horizon', 'factor_kind',
    'factor_release_id', 'factor_deployment_id', 'factor_as_of_date',
]);

// Every published-context field, in a fixed order: the exact update a user
// accepted or declined, including MSI and provider-mapping maintenance.
const CONTEXT_UPDATE_FIELDS = Object.freeze([
    ...FACTOR_DATA_UPDATE_FIELDS,
    'provider_source_system', 'provider_namespace', 'provider_source_release_id',
    'msi_snapshot_version', 'setting_version', 'setting_digest',
    'route_policy_contract', 'route_policy_version', 'guarded_contract_version',
]);

export function hasFactorDataUpdate(context) {
    if (context?.status !== 'update_available') return false;
    if (context.requires_initial_validation) return false;
    return FACTOR_DATA_UPDATE_FIELDS.some((field) => (
        (context.saved?.[field] ?? '') !== (context.available?.[field] ?? '')
    ));
}

export function factorDataUpdateKey(context) {
    return JSON.stringify(FACTOR_DATA_UPDATE_FIELDS.map((field) => (
        context?.available?.[field] ?? null
    )));
}

export function contextUpdateKey(available) {
    return JSON.stringify(CONTEXT_UPDATE_FIELDS.map((field) => available?.[field] ?? null));
}

export function factorDataUpdatedNotice(available) {
    const date = /^\d{4}-\d{2}-\d{2}$/.test(String(available?.factor_as_of_date || ''))
        ? new Date(`${available.factor_as_of_date}T00:00:00`).toLocaleDateString('en-US', {
            month: 'short', day: 'numeric', year: 'numeric',
        })
        : '';
    return date
        ? `Your Watchlist now uses factor data from ${date}.`
        : 'Your Watchlist now uses the new factor data.';
}

// A factor vendor chosen in Settings is saved only when its Watchlist re-check
// publishes, so each message names the vendor the Watchlist keeps meanwhile.
// A switch in a running state has pinned the setting and missing-factor
// policy it evaluates.
export const FACTOR_VENDOR_SWITCH_RUNNING_STATES = Object.freeze(['switching', 'review']);

export function isFactorVendorSwitchRunning(vendorSwitch) {
    return FACTOR_VENDOR_SWITCH_RUNNING_STATES.includes(vendorSwitch?.state);
}

export function factorVendorSwitchingText(target) {
    return `Switching to ${formatUserFactorModel(target)}…`;
}

export function factorVendorSwitchedNotice(target) {
    return `Switched to ${formatUserFactorModel(target)}. Your Watchlist and risk data are updated.`;
}

export function factorVendorSwitchFailedMessage(target, current) {
    return `The switch to ${formatUserFactorModel(target)} didn't complete; your factor data `
        + `source is still ${formatUserFactorModel(current)}. Try again from Settings.`;
}

// A switch that waits on a Watchlist change, or that one replaced, is held only
// by the open Watchlist page of this browser session, so both say what to do
// if it has not started.
export function factorVendorSwitchWaitingMessage(target, current) {
    const vendor = formatUserFactorModel(target);
    return `Your Watchlist change is still being applied, so your factor data source is `
        + `still ${formatUserFactorModel(current)}. If the switch to ${vendor} hasn't `
        + `started when it finishes, choose ${vendor} again in Settings.`;
}

export function factorVendorSwitchReplacedMessage(target, current) {
    const vendor = formatUserFactorModel(target);
    return `Your Watchlist change replaced the switch to ${vendor}, so your factor data `
        + `source is still ${formatUserFactorModel(current)}. If the switch hasn't started `
        + `again when that change finishes, choose ${vendor} again in Settings.`;
}

export function factorDataDisplayContext(context) {
    if (!context) return null;
    return {
        factorModel: context.vendor,
        factorDataModel: [context.factor_model, context.factor_horizon].filter(Boolean).join('-'),
        factorSnapshotDate: context.factor_as_of_date,
        factorRelease: context.factor_release_id,
    };
}

export function factorDataUpdateExplanation(context) {
    const sameModelAndDate = context?.saved && context?.available
        && ['vendor', 'factor_model', 'factor_horizon', 'factor_as_of_date'].every(
            field => context.saved[field] === context.available[field],
        );
    const revision = sameModelAndDate
        ? 'A newer revision has been published for the same model and date. Those labels are unchanged, but this is a separate published dataset. '
        : '';
    return `${revision}Using it re-checks your current Watchlist against the new data. If the securities in your Watchlist stay the same, your Watchlist and Actual Portfolio switch to it right away; otherwise you review the changes first. A Watchlist change you have not applied yet is not affected.`;
}
